import api from './api';

export interface PDFSettings {
  id?: number;
  name: string;
  description?: string;

  // Layout options
  layout: 'professional' | 'compact';
  color_scheme: 'blue' | 'green' | 'gray' | 'red' | 'purple' | 'orange';
  font_family: 'helvetica' | 'times' | 'courier';
  font_size: 'small' | 'medium' | 'large';
  spacing: 'tight' | 'normal' | 'loose';
  border_style: 'none' | 'simple' | 'rounded';

  // Display toggles
  include_company_logo: boolean;
  show_internal_notes: boolean;
  show_part_details: boolean;
  show_page_numbers: boolean;

  // Discount options
  show_discount_breakdown: boolean;
  highlight_discounts: boolean;
  show_original_prices: boolean;

  // Company information
  company_name: string;
  company_address?: string;
  company_city?: string;
  company_state?: string;
  company_zip?: string;
  company_phone: string;
  company_email: string;
  company_website?: string;
  company_logo?: string; // base64 encoded logo

  // Metadata
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CompanyInfo {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone: string;
  email: string;
  website?: string;
  logo?: string;
}

export interface PDFLayoutOptions {
  layout: 'professional' | 'compact';
  includeCompanyLogo: boolean;
  showInternalNotes: boolean;
  showPartDetails: boolean;
  showDiscountBreakdown: boolean;
  highlightDiscounts: boolean;
  showOriginalPrices: boolean;
  colorScheme: string;
  fontFamily: string;
  fontSize: string;
  spacing: string;
  borderStyle: string;
  showPageNumbers: boolean;
}

export class PDFSettingsApiService {
  /**
   * Get the default PDF settings
   */
  static async getDefaultSettings(): Promise<PDFSettings> {
    try {
      const response = await api.get('/pdf-settings/default');
      return response.data;
    } catch (error) {
      console.error('Failed to get default PDF settings:', error);
      throw new Error('Failed to load PDF settings');
    }
  }

  /**
   * Update the default PDF settings
   */
  static async updateDefaultSettings(settings: Partial<PDFSettings>): Promise<PDFSettings> {
    try {
      console.log('🔄 API Service - Received settings:', settings);

      // Convert frontend field names to backend field names
      const backendSettings = this.mapToBackendFormat(settings);

      console.log('🔄 API Service - After mapping:', backendSettings);
      console.log('🌐 Sending to backend at /pdf-settings/default/update');

      const response = await api.put('/pdf-settings/default/update', backendSettings);
      return response.data;
    } catch (error) {
      console.error('Failed to update default PDF settings:', error);
      throw new Error('Failed to save PDF settings');
    }
  }

  /**
   * Get all PDF settings
   */
  static async getAllSettings(): Promise<PDFSettings[]> {
    try {
      const response = await api.get('/pdf-settings');
      return response.data;
    } catch (error) {
      console.error('Failed to get all PDF settings:', error);
      throw new Error('Failed to load PDF settings');
    }
  }

  /**
   * Get PDF settings by ID
   */
  static async getSettingsById(id: number): Promise<PDFSettings> {
    try {
      const response = await api.get(`/pdf-settings/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get PDF settings by ID:', error);
      throw new Error('Failed to load PDF settings');
    }
  }

  /**
   * Create new PDF settings
   */
  static async createSettings(settings: Omit<PDFSettings, 'id' | 'created_at' | 'updated_at'>): Promise<PDFSettings> {
    try {
      const backendSettings = this.mapToBackendFormat(settings);
      const response = await api.post('/pdf-settings', backendSettings);
      return response.data;
    } catch (error) {
      console.error('Failed to create PDF settings:', error);
      throw new Error('Failed to create PDF settings');
    }
  }

  /**
   * Delete PDF settings
   */
  static async deleteSettings(id: number): Promise<void> {
    try {
      await api.delete(`/pdf-settings/${id}`);
    } catch (error) {
      console.error('Failed to delete PDF settings:', error);
      throw new Error('Failed to delete PDF settings');
    }
  }

  /**
   * Reset to default settings
   */
  static async resetToDefault(): Promise<PDFSettings> {
    try {
      const response = await api.post('/pdf-settings/reset-default');
      return response.data;
    } catch (error) {
      console.error('Failed to reset to default PDF settings:', error);
      throw new Error('Failed to reset PDF settings');
    }
  }

  /**
   * Get layout options only (for backwards compatibility)
   */
  static async getDefaultLayoutOptions(): Promise<PDFLayoutOptions> {
    const settings = await this.getDefaultSettings();
    return this.mapToLayoutOptions(settings);
  }

  /**
   * Get company info only (for backwards compatibility)
   */
  static async getDefaultCompanyInfo(): Promise<CompanyInfo> {
    const settings = await this.getDefaultSettings();
    return this.mapToCompanyInfo(settings);
  }

  /**
   * Update company information
   */
  static async updateCompanyInfo(companyInfo: Partial<CompanyInfo>): Promise<PDFSettings> {
    const backendCompanyInfo = {
      company_name: companyInfo.name,
      company_address: companyInfo.address,
      company_city: companyInfo.city,
      company_state: companyInfo.state,
      company_zip: companyInfo.zip,
      company_phone: companyInfo.phone,
      company_email: companyInfo.email,
      company_website: companyInfo.website,
      company_logo: companyInfo.logo,
    };

    return this.updateDefaultSettings(backendCompanyInfo);
  }

  /**
   * Update layout options
   */
  static async updateLayoutOptions(options: Partial<PDFLayoutOptions>): Promise<PDFSettings> {
    const backendOptions: any = {
      layout: options.layout,
      include_company_logo: options.includeCompanyLogo,
      show_internal_notes: options.showInternalNotes,
      show_part_details: options.showPartDetails,
      show_discount_breakdown: options.showDiscountBreakdown,
      highlight_discounts: options.highlightDiscounts,
      show_original_prices: options.showOriginalPrices,
      color_scheme: options.colorScheme,
      font_family: options.fontFamily,
      font_size: options.fontSize,
      spacing: options.spacing,
      border_style: options.borderStyle,
      show_page_numbers: options.showPageNumbers,
    };

    return this.updateDefaultSettings(backendOptions);
  }

  /**
   * Check if custom settings exist
   */
  static async hasCustomSettings(): Promise<boolean> {
    try {
      const settings = await this.getDefaultSettings();
      // If settings have been created/updated, they should have an ID
      return !!settings.id;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate company logo (base64 or URL)
   */
  static validateLogo(logo: string): boolean {
    if (!logo) return true; // Logo is optional

    // Check if it's a data URL (base64)
    if (logo.startsWith('data:image/')) {
      return true;
    }

    // Check if it's a valid URL
    try {
      new URL(logo);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Map frontend settings format to backend format
   */
  private static mapToBackendFormat(settings: any): any {
    const mapped: any = {};

    // Direct mappings
    if (settings.name !== undefined) mapped.name = settings.name;
    if (settings.description !== undefined) mapped.description = settings.description;
    if (settings.layout !== undefined) mapped.layout = settings.layout;
    if (settings.is_default !== undefined) mapped.is_default = settings.is_default;

    // Field name mappings (frontend camelCase to backend snake_case)
    const fieldMappings = {
      colorScheme: 'color_scheme',
      fontFamily: 'font_family',
      fontSize: 'font_size',
      borderStyle: 'border_style',
      includeCompanyLogo: 'include_company_logo',
      showInternalNotes: 'show_internal_notes',
      showPartDetails: 'show_part_details',
      showPageNumbers: 'show_page_numbers',
      showDiscountBreakdown: 'show_discount_breakdown',
      highlightDiscounts: 'highlight_discounts',
      showOriginalPrices: 'show_original_prices',
      companyName: 'company_name',
      companyAddress: 'company_address',
      companyCity: 'company_city',
      companyState: 'company_state',
      companyZip: 'company_zip',
      companyPhone: 'company_phone',
      companyEmail: 'company_email',
      companyWebsite: 'company_website',
      companyLogo: 'company_logo',
    };

    for (const [frontendKey, backendKey] of Object.entries(fieldMappings)) {
      if (settings[frontendKey] !== undefined) {
        mapped[backendKey] = settings[frontendKey];
      }
    }

    // Also handle direct backend field names
    const backendFields = [
      'color_scheme', 'font_family', 'font_size', 'border_style', 'spacing',
      'include_company_logo', 'show_internal_notes', 'show_part_details', 'show_page_numbers',
      'show_discount_breakdown', 'highlight_discounts', 'show_original_prices',
      'company_name', 'company_address', 'company_city', 'company_state', 'company_zip',
      'company_phone', 'company_email', 'company_website', 'company_logo'
    ];

    for (const field of backendFields) {
      if (settings[field] !== undefined) {
        mapped[field] = settings[field];
      }
    }

    return mapped;
  }


  /**
   * Map backend settings to frontend company info format
   */
  static mapToCompanyInfo(settings: PDFSettings): CompanyInfo {
    return {
      name: settings.company_name,
      address: settings.company_address,
      city: settings.company_city,
      state: settings.company_state,
      zip: settings.company_zip,
      phone: settings.company_phone,
      email: settings.company_email,
      website: settings.company_website,
      logo: settings.company_logo,
    };
  }

  /**
   * Map backend settings to frontend layout options format
   */
  static mapToLayoutOptions(settings: PDFSettings): PDFLayoutOptions {
    return {
      layout: settings.layout,
      includeCompanyLogo: settings.include_company_logo,
      showInternalNotes: settings.show_internal_notes,
      showPartDetails: settings.show_part_details,
      showDiscountBreakdown: settings.show_discount_breakdown,
      highlightDiscounts: settings.highlight_discounts,
      showOriginalPrices: settings.show_original_prices,
      colorScheme: settings.color_scheme,
      fontFamily: settings.font_family,
      fontSize: settings.font_size,
      spacing: settings.spacing,
      borderStyle: settings.border_style,
      showPageNumbers: settings.show_page_numbers,
    };
  }
}